import {
  CalendarDays,
  Check,
  ChevronRight,
  CircleDashed,
  Clock3,
  FileCheck2,
  MapPin,
  RotateCcw,
  Search,
  ShieldAlert,
  UserRound,
  UsersRound,
  X,
  XCircle,
} from "lucide-react";
import { useId, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  EmptyState,
  InlineNotice,
  Modal,
  PageHeader,
} from "../components/ui";
import { formatDayDate, formatTime, todayISO } from "../lib/utils";
import { useApp } from "../state/AppContext";
import type {
  AttendanceRecord,
  AttendanceStatus,
  SchoolClass,
  Student,
} from "../types";
import "../styles/classes.css";

type RegisterFilter = "all" | "unrecorded" | "complete" | "absent";
type SortOption = "class" | "progress" | "unrecorded" | "absent";
type YearFilter = "all" | number;

interface ClassRegisterRow {
  schoolClass: SchoolClass;
  rosterCount: number;
  activeCount: number;
  recordedCount: number;
  present: number;
  late: number;
  absent: number;
  excused: number;
  unrecorded: number;
  progress: number;
  inactiveCount: number;
  latestTimestamp?: string;
  unrecordedStudents: Student[];
}

const emptyStatusCounts = (): Record<AttendanceStatus, number> => ({
  present: 0,
  late: 0,
  absent: 0,
  excused: 0,
});

const normalizeSearch = (value: string) =>
  value.trim().toLocaleLowerCase("ms-MY");

function RegisterProgress({
  recorded,
  expected,
  progress,
  className = "",
}: {
  recorded: number;
  expected: number;
  progress: number;
  className?: string;
}) {
  return (
    <div
      className={`classes-progress ${className}`}
      role="progressbar"
      aria-label={`${recorded} daripada ${expected} murid telah direkodkan`}
      aria-valuemin={0}
      aria-valuemax={expected}
      aria-valuenow={recorded}
    >
      <div className="classes-progress__meta">
        <strong>{progress}%</strong>
        <span>
          {recorded}/{expected}
        </span>
      </div>
      <span className="classes-progress__track" aria-hidden="true">
        <span style={{ width: `${progress}%` }} />
      </span>
    </div>
  );
}

function RestrictedClassesPage() {
  return (
    <div className="classes-page">
      <PageHeader
        eyebrow="Pentadbiran sekolah"
        title="Kelas & kemajuan daftar"
        description="Pantau penugasan kelas dan kemajuan rekod kehadiran harian."
      />

      <section className="classes-restricted" aria-labelledby="classes-restricted-title">
        <span className="classes-restricted__icon" aria-hidden="true">
          <ShieldAlert size={28} />
        </span>
        <div>
          <span className="eyebrow">Akses terhad</span>
          <h2 id="classes-restricted-title">Paparan ini untuk pentadbir</h2>
          <p>
            Maklumat merentas semua kelas, guru dan bilik hanya boleh dilihat oleh
            akaun pentadbir sekolah.
          </p>
          <InlineNotice tone="neutral" title="Akaun guru">
            Gunakan halaman Kehadiran untuk menyemak daftar kelas yang diberikan
            kepada anda.
          </InlineNotice>
        </div>
      </section>
    </div>
  );
}

function AdminClassesPage() {
  const { attendance, classes, students } = useApp();
  const [query, setQuery] = useState("");
  const [yearFilter, setYearFilter] = useState<YearFilter>("all");
  const [registerFilter, setRegisterFilter] =
    useState<RegisterFilter>("all");
  const [sortBy, setSortBy] = useState<SortOption>("class");
  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);
  const searchId = useId();
  const yearFilterId = useId();
  const registerFilterId = useId();
  const sortId = useId();
  const today = todayISO();

  const rows = useMemo<ClassRegisterRow[]>(() => {
    const studentById = new Map(students.map((student) => [student.id, student]));
    const latestRecordByStudent = new Map<string, AttendanceRecord>();

    attendance.forEach((record) => {
      if (record.date !== today) return;
      const student = studentById.get(record.studentId);
      if (!student?.active) return;

      const current = latestRecordByStudent.get(record.studentId);
      if (
        !current ||
        new Date(record.timestamp).getTime() >
          new Date(current.timestamp).getTime()
      ) {
        latestRecordByStudent.set(record.studentId, record);
      }
    });

    return classes.map((schoolClass) => {
      const roster = students.filter(
        (student) => student.classId === schoolClass.id,
      );
      const activeRoster = roster.filter((student) => student.active);
      const records = activeRoster
        .map((student) => latestRecordByStudent.get(student.id))
        .filter((record): record is AttendanceRecord => Boolean(record));
      const statusCounts = emptyStatusCounts();

      records.forEach((record) => {
        statusCounts[record.status] += 1;
      });

      const recordedStudentIds = new Set(
        records.map((record) => record.studentId),
      );
      const unrecordedStudents = activeRoster.filter(
        (student) => !recordedStudentIds.has(student.id),
      );
      const recordedCount = records.length;
      const activeCount = activeRoster.length;

      return {
        schoolClass,
        rosterCount: roster.length,
        activeCount,
        recordedCount,
        present: statusCounts.present,
        late: statusCounts.late,
        absent: statusCounts.absent,
        excused: statusCounts.excused,
        unrecorded: unrecordedStudents.length,
        progress:
          activeCount === 0
            ? 0
            : Math.round((recordedCount / activeCount) * 100),
        inactiveCount: roster.length - activeCount,
        latestTimestamp: records.reduce<string | undefined>(
          (latest, record) =>
            !latest ||
            new Date(record.timestamp).getTime() >
              new Date(latest).getTime()
              ? record.timestamp
              : latest,
          undefined,
        ),
        unrecordedStudents,
      };
    });
  }, [attendance, classes, students, today]);

  const totals = useMemo(() => {
    const summary = rows.reduce(
      (current, row) => ({
        roster: current.roster + row.rosterCount,
        active: current.active + row.activeCount,
        recorded: current.recorded + row.recordedCount,
        present: current.present + row.present,
        late: current.late + row.late,
        absent: current.absent + row.absent,
        excused: current.excused + row.excused,
        unrecorded: current.unrecorded + row.unrecorded,
        completeClasses:
          current.completeClasses + (row.unrecorded === 0 ? 1 : 0),
      }),
      {
        roster: 0,
        active: 0,
        recorded: 0,
        present: 0,
        late: 0,
        absent: 0,
        excused: 0,
        unrecorded: 0,
        completeClasses: 0,
      },
    );

    const latestTimestamp = rows.reduce<string | undefined>(
      (latest, row) =>
        row.latestTimestamp &&
        (!latest ||
          new Date(row.latestTimestamp).getTime() >
            new Date(latest).getTime())
          ? row.latestTimestamp
          : latest,
      undefined,
    );

    return {
      ...summary,
      progress:
        summary.active === 0
          ? 0
          : Math.round((summary.recorded / summary.active) * 100),
      latestTimestamp,
    };
  }, [rows]);

  const visibleRows = useMemo(() => {
    const normalizedQuery = normalizeSearch(query);
    const filtered = rows.filter((row) => {
      const matchesQuery =
        normalizedQuery.length === 0 ||
        [
          row.schoolClass.name,
          row.schoolClass.teacher,
          row.schoolClass.room,
          `tahun ${row.schoolClass.year}`,
        ].some((value) =>
          value.toLocaleLowerCase("ms-MY").includes(normalizedQuery),
        );
      const matchesYear =
        yearFilter === "all" || row.schoolClass.year === yearFilter;
      const matchesRegister =
        registerFilter === "all" ||
        (registerFilter === "unrecorded" && row.unrecorded > 0) ||
        (registerFilter === "complete" && row.unrecorded === 0) ||
        (registerFilter === "absent" && row.absent > 0);

      return matchesQuery && matchesYear && matchesRegister;
    });

    return [...filtered].sort((left, right) => {
      if (sortBy === "progress") {
        return (
          left.progress - right.progress ||
          left.schoolClass.year - right.schoolClass.year ||
          left.schoolClass.stream.localeCompare(right.schoolClass.stream, "ms")
        );
      }
      if (sortBy === "unrecorded") {
        return (
          right.unrecorded - left.unrecorded ||
          left.schoolClass.year - right.schoolClass.year ||
          left.schoolClass.stream.localeCompare(right.schoolClass.stream, "ms")
        );
      }
      if (sortBy === "absent") {
        return (
          right.absent - left.absent ||
          left.schoolClass.year - right.schoolClass.year ||
          left.schoolClass.stream.localeCompare(right.schoolClass.stream, "ms")
        );
      }
      return (
        left.schoolClass.year - right.schoolClass.year ||
        left.schoolClass.stream.localeCompare(right.schoolClass.stream, "ms")
      );
    });
  }, [query, registerFilter, rows, sortBy, yearFilter]);

  const selectedRow =
    rows.find((row) => row.schoolClass.id === selectedClassId) ?? null;
  const yearOptions = [...new Set(classes.map((schoolClass) => schoolClass.year))].sort(
    (left, right) => left - right,
  );
  const filtersActive =
    query.trim().length > 0 ||
    yearFilter !== "all" ||
    registerFilter !== "all" ||
    sortBy !== "class";

  const resetFilters = () => {
    setQuery("");
    setYearFilter("all");
    setRegisterFilter("all");
    setSortBy("class");
  };

  return (
    <div className="classes-page">
      <PageHeader
        eyebrow="Pentadbiran sekolah"
        title="Kelas & kemajuan daftar"
        description={`${formatDayDate(today)} · Pantau penugasan dan rekod kehadiran bagi semua kelas.`}
        actions={
          <div className="classes-date-stamp" aria-label={`Tarikh daftar ${formatDayDate(today)}`}>
            <CalendarDays size={18} aria-hidden="true" />
            <span>
              <small>Daftar hari ini</small>
              <strong>{formatDayDate(today)}</strong>
            </span>
          </div>
        }
      />

      <section className="classes-register" aria-labelledby="register-overview-title">
        <div className="classes-register__overview">
          <div
            className="classes-register__dial"
            style={{
              background: `conic-gradient(var(--primary) ${totals.progress}%, var(--surface-strong) 0)`,
            }}
            role="progressbar"
            aria-label={`Kemajuan keseluruhan ${totals.progress} peratus`}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={totals.progress}
          >
            <span>
              <strong>{totals.progress}%</strong>
              <small>direkod</small>
            </span>
          </div>
          <div className="classes-register__copy">
            <span className="classes-register__kicker">Nadi daftar pagi</span>
            <h2 id="register-overview-title">
              {totals.recorded} daripada {totals.active} murid aktif telah direkod
            </h2>
            <p>
              {totals.unrecorded > 0
                ? `${totals.unrecorded} murid dalam ${rows.filter((row) => row.unrecorded > 0).length} kelas masih belum mempunyai rekod hari ini.`
                : "Semua kelas telah melengkapkan daftar kehadiran hari ini."}
            </p>
            <span className="classes-register__meta">
              {rows.length} kelas · {totals.roster} murid dalam senarai ·{" "}
              {totals.completeClasses} kelas lengkap
              {totals.latestTimestamp
                ? ` · Rekod terakhir ${formatTime(totals.latestTimestamp)}`
                : ""}
            </span>
          </div>
        </div>

        <dl className="classes-register__counts" aria-label="Ringkasan status hari ini">
          <div className="classes-register__count classes-register__count--present">
            <dt>
              <Check size={16} aria-hidden="true" />
              Hadir
            </dt>
            <dd>{totals.present}</dd>
          </div>
          <div className="classes-register__count classes-register__count--late">
            <dt>
              <Clock3 size={16} aria-hidden="true" />
              Lewat
            </dt>
            <dd>{totals.late}</dd>
          </div>
          <div className="classes-register__count classes-register__count--absent">
            <dt>
              <XCircle size={16} aria-hidden="true" />
              Tidak hadir
            </dt>
            <dd>{totals.absent}</dd>
          </div>
          <div className="classes-register__count classes-register__count--excused">
            <dt>
              <FileCheck2 size={16} aria-hidden="true" />
              Berizin
            </dt>
            <dd>{totals.excused}</dd>
          </div>
          <div className="classes-register__count classes-register__count--unrecorded">
            <dt>
              <CircleDashed size={16} aria-hidden="true" />
              Belum direkod
            </dt>
            <dd>{totals.unrecorded}</dd>
          </div>
        </dl>
      </section>

      <section className="classes-workspace" aria-labelledby="classes-list-title">
        <header className="classes-workspace__header">
          <div>
            <span className="eyebrow">Semakan kelas</span>
            <h2 id="classes-list-title">Daftar mengikut kelas</h2>
            <p>
              Menunjukkan {visibleRows.length} daripada {rows.length} kelas
            </p>
          </div>
          <Button
            variant="secondary"
            size="compact"
            icon={RotateCcw}
            onClick={resetFilters}
            disabled={!filtersActive}
          >
            Tetapkan semula
          </Button>
        </header>

        <div className="classes-toolbar" role="search">
          <div className="classes-search">
            <label className="sr-only" htmlFor={searchId}>
              Cari kelas, guru atau bilik
            </label>
            <Search size={18} aria-hidden="true" />
            <input
              id={searchId}
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Cari kelas, guru atau bilik"
            />
            {query && (
              <button
                type="button"
                className="classes-search__clear"
                aria-label="Kosongkan carian"
                onClick={() => setQuery("")}
              >
                <X size={16} />
              </button>
            )}
          </div>

          <div className="classes-toolbar__select">
            <label htmlFor={yearFilterId}>Tahun</label>
            <select
              id={yearFilterId}
              value={yearFilter}
              onChange={(event) =>
                setYearFilter(
                  event.target.value === "all"
                    ? "all"
                    : Number(event.target.value),
                )
              }
            >
              <option value="all">Semua tahun</option>
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  Tahun {year}
                </option>
              ))}
            </select>
          </div>

          <div className="classes-toolbar__select">
            <label htmlFor={registerFilterId}>Status daftar</label>
            <select
              id={registerFilterId}
              value={registerFilter}
              onChange={(event) =>
                setRegisterFilter(event.target.value as RegisterFilter)
              }
            >
              <option value="all">Semua status</option>
              <option value="unrecorded">Masih belum direkod</option>
              <option value="complete">Daftar lengkap</option>
              <option value="absent">Ada murid tidak hadir</option>
            </select>
          </div>

          <div className="classes-toolbar__select">
            <label htmlFor={sortId}>Susun</label>
            <select
              id={sortId}
              value={sortBy}
              onChange={(event) => setSortBy(event.target.value as SortOption)}
            >
              <option value="class">Tahun & kelas</option>
              <option value="progress">Kemajuan terendah</option>
              <option value="unrecorded">Belum direkod terbanyak</option>
              <option value="absent">Tidak hadir terbanyak</option>
            </select>
          </div>
        </div>

        {visibleRows.length === 0 ? (
          <EmptyState
            icon={Search}
            title="Tiada kelas sepadan"
            message="Cuba kata carian lain atau tetapkan semula penapis untuk melihat semua kelas."
            action={
              <Button variant="secondary" icon={RotateCcw} onClick={resetFilters}>
                Lihat semua kelas
              </Button>
            }
          />
        ) : (
          <>
            <div className="classes-table-view">
              <div className="classes-table-wrap">
                <table className="classes-table">
                  <caption className="sr-only">
                    Kemajuan daftar kehadiran untuk {visibleRows.length} kelas pada{" "}
                    {formatDayDate(today)}
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">Kelas</th>
                      <th scope="col">Guru & bilik</th>
                      <th scope="col">Kemajuan</th>
                      <th scope="col" className="classes-table__number">
                        Hadir
                      </th>
                      <th scope="col" className="classes-table__number">
                        Lewat
                      </th>
                      <th scope="col" className="classes-table__number">
                        Tidak hadir
                      </th>
                      <th scope="col" className="classes-table__number">
                        Berizin
                      </th>
                      <th scope="col" className="classes-table__number">
                        Belum
                      </th>
                      <th scope="col">
                        <span className="sr-only">Tindakan</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((row, index) => (
                      <tr key={row.schoolClass.id}>
                        <th scope="row">
                          <div className="classes-class-cell">
                            <span className="classes-class-cell__year" aria-hidden="true">
                              {row.schoolClass.year}
                            </span>
                            <span>
                              <strong>{row.schoolClass.name}</strong>
                              <small>
                                {row.rosterCount} murid
                                {row.inactiveCount > 0
                                  ? ` · ${row.inactiveCount} tidak aktif`
                                  : ""}
                              </small>
                            </span>
                          </div>
                        </th>
                        <td>
                          <div className="classes-assignment">
                            <Avatar
                              initials={row.schoolClass.teacherInitials}
                              size="sm"
                              tone={index}
                              label={`Inisial ${row.schoolClass.teacher}`}
                            />
                            <span>
                              <strong>{row.schoolClass.teacher}</strong>
                              <small>
                                <MapPin size={13} aria-hidden="true" />
                                {row.schoolClass.room}
                              </small>
                            </span>
                          </div>
                        </td>
                        <td>
                          <RegisterProgress
                            recorded={row.recordedCount}
                            expected={row.activeCount}
                            progress={row.progress}
                          />
                        </td>
                        <td className="classes-table__number">
                          <span className="classes-count classes-count--present">
                            {row.present}
                          </span>
                        </td>
                        <td className="classes-table__number">
                          <span className="classes-count classes-count--late">
                            {row.late}
                          </span>
                        </td>
                        <td className="classes-table__number">
                          <span className="classes-count classes-count--absent">
                            {row.absent}
                          </span>
                        </td>
                        <td className="classes-table__number">
                          <span className="classes-count classes-count--excused">
                            {row.excused}
                          </span>
                        </td>
                        <td className="classes-table__number">
                          <span
                            className={`classes-count ${
                              row.unrecorded > 0
                                ? "classes-count--unrecorded"
                                : "classes-count--complete"
                            }`}
                          >
                            {row.unrecorded}
                          </span>
                        </td>
                        <td className="classes-table__action">
                          <button
                            type="button"
                            className="classes-detail-button"
                            aria-label={`Lihat perincian ${row.schoolClass.name}`}
                            aria-haspopup="dialog"
                            onClick={() =>
                              setSelectedClassId(row.schoolClass.id)
                            }
                          >
                            <ChevronRight size={19} aria-hidden="true" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="classes-card-list">
              {visibleRows.map((row, index) => (
                <article className="classes-class-card" key={row.schoolClass.id}>
                  <header className="classes-class-card__header">
                    <div className="classes-class-cell">
                      <span className="classes-class-cell__year" aria-hidden="true">
                        {row.schoolClass.year}
                      </span>
                      <span>
                        <h3>{row.schoolClass.name}</h3>
                        <small>
                          {row.rosterCount} murid dalam senarai
                          {row.inactiveCount > 0
                            ? ` · ${row.inactiveCount} tidak aktif`
                            : ""}
                        </small>
                      </span>
                    </div>
                    <span
                      className={`classes-register-state ${
                        row.unrecorded === 0
                          ? "classes-register-state--complete"
                          : ""
                      }`}
                    >
                      {row.unrecorded === 0
                        ? "Lengkap"
                        : `${row.unrecorded} belum`}
                    </span>
                  </header>

                  <div className="classes-class-card__assignment">
                    <Avatar
                      initials={row.schoolClass.teacherInitials}
                      size="sm"
                      tone={index}
                      label={`Inisial ${row.schoolClass.teacher}`}
                    />
                    <span>
                      <strong>{row.schoolClass.teacher}</strong>
                      <small>
                        <MapPin size={13} aria-hidden="true" />
                        {row.schoolClass.room}
                      </small>
                    </span>
                  </div>

                  <RegisterProgress
                    recorded={row.recordedCount}
                    expected={row.activeCount}
                    progress={row.progress}
                    className="classes-progress--card"
                  />

                  <dl className="classes-class-card__counts">
                    <div>
                      <dt>Hadir</dt>
                      <dd className="classes-count--present">{row.present}</dd>
                    </div>
                    <div>
                      <dt>Lewat</dt>
                      <dd className="classes-count--late">{row.late}</dd>
                    </div>
                    <div>
                      <dt>Tidak hadir</dt>
                      <dd className="classes-count--absent">{row.absent}</dd>
                    </div>
                    <div>
                      <dt>Berizin</dt>
                      <dd className="classes-count--excused">{row.excused}</dd>
                    </div>
                  </dl>

                  <button
                    type="button"
                    className="classes-card-detail"
                    aria-haspopup="dialog"
                    onClick={() => setSelectedClassId(row.schoolClass.id)}
                  >
                    Lihat perincian kelas
                    <ChevronRight size={18} aria-hidden="true" />
                  </button>
                </article>
              ))}
            </div>
          </>
        )}
      </section>

      <Modal
        open={Boolean(selectedRow)}
        onClose={() => setSelectedClassId(null)}
        title={selectedRow ? `Kelas ${selectedRow.schoolClass.name}` : "Perincian kelas"}
        description={
          selectedRow
            ? `${selectedRow.schoolClass.teacher} · ${selectedRow.schoolClass.room}`
            : undefined
        }
        size="lg"
        footer={
          <Button
            variant="secondary"
            onClick={() => setSelectedClassId(null)}
          >
            Tutup
          </Button>
        }
      >
        {selectedRow && (
          <div className="classes-detail">
            <section
              className="classes-detail__progress"
              aria-labelledby="class-detail-progress-title"
            >
              <div>
                <span className="classes-register__kicker">Kemajuan hari ini</span>
                <h3 id="class-detail-progress-title">
                  {selectedRow.recordedCount} daripada {selectedRow.activeCount} murid
                  aktif
                </h3>
                <p>
                  {selectedRow.unrecorded > 0
                    ? `${selectedRow.unrecorded} rekod masih diperlukan untuk melengkapkan daftar.`
                    : "Daftar kelas ini telah lengkap."}
                </p>
              </div>
              <strong>{selectedRow.progress}%</strong>
              <RegisterProgress
                recorded={selectedRow.recordedCount}
                expected={selectedRow.activeCount}
                progress={selectedRow.progress}
                className="classes-progress--detail"
              />
            </section>

            <div className="classes-detail__grid">
              <section aria-labelledby="class-detail-status-title">
                <h3 id="class-detail-status-title">Pecahan daftar</h3>
                <dl className="classes-detail__status-list">
                  <div>
                    <dt>
                      <Check size={16} aria-hidden="true" />
                      Hadir
                    </dt>
                    <dd>{selectedRow.present}</dd>
                  </div>
                  <div>
                    <dt>
                      <Clock3 size={16} aria-hidden="true" />
                      Lewat
                    </dt>
                    <dd>{selectedRow.late}</dd>
                  </div>
                  <div>
                    <dt>
                      <XCircle size={16} aria-hidden="true" />
                      Tidak hadir
                    </dt>
                    <dd>{selectedRow.absent}</dd>
                  </div>
                  <div>
                    <dt>
                      <FileCheck2 size={16} aria-hidden="true" />
                      Berizin
                    </dt>
                    <dd>{selectedRow.excused}</dd>
                  </div>
                  <div>
                    <dt>
                      <CircleDashed size={16} aria-hidden="true" />
                      Belum direkod
                    </dt>
                    <dd>{selectedRow.unrecorded}</dd>
                  </div>
                </dl>
              </section>

              <section aria-labelledby="class-detail-assignment-title">
                <h3 id="class-detail-assignment-title">Penugasan kelas</h3>
                <dl className="classes-detail__assignment-list">
                  <div>
                    <dt>
                      <UserRound size={16} aria-hidden="true" />
                      Guru kelas
                    </dt>
                    <dd>{selectedRow.schoolClass.teacher}</dd>
                  </div>
                  <div>
                    <dt>
                      <MapPin size={16} aria-hidden="true" />
                      Bilik
                    </dt>
                    <dd>{selectedRow.schoolClass.room}</dd>
                  </div>
                  <div>
                    <dt>
                      <UsersRound size={16} aria-hidden="true" />
                      Senarai murid
                    </dt>
                    <dd>
                      {selectedRow.rosterCount} murid · {selectedRow.activeCount} aktif
                    </dd>
                  </div>
                </dl>
              </section>
            </div>

            <section
              className="classes-detail__pending"
              aria-labelledby="class-detail-pending-title"
            >
              <div>
                <h3 id="class-detail-pending-title">Murid belum direkod</h3>
                <p>Senarai ini hanya mengambil kira profil murid yang aktif.</p>
              </div>
              {selectedRow.unrecordedStudents.length > 0 ? (
                <ul>
                  {selectedRow.unrecordedStudents.map((student, index) => (
                    <li key={student.id}>
                      <Avatar
                        initials={student.initials}
                        size="sm"
                        tone={index + 2}
                        label={`Inisial ${student.name}`}
                      />
                      <span>
                        <strong>{student.name}</strong>
                        <small>{student.code}</small>
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <InlineNotice tone="success" title="Daftar lengkap">
                  Semua murid aktif mempunyai rekod kehadiran hari ini.
                </InlineNotice>
              )}
            </section>
          </div>
        )}
      </Modal>
    </div>
  );
}

export function ClassesPage() {
  const { session } = useApp();

  if (session?.role !== "admin") {
    return <RestrictedClassesPage />;
  }

  return <AdminClassesPage />;
}
